import {Injectable} from "@angular/core";
import {Observable} from "rxjs";

import {Controller} from "../../interface/controller.interface";
import {
    ActionPointCampaignConfiguration
} from "../../model/club-gigot/action-points/action-points-campaign-configuration.model";
import {
    ActionPointsDTO,
    AdvanceSaleLevel,
    AdvanceSaleRequiredProductGroup,
    ExtraPoints,
} from "../../model/club-gigot/action-points/action-points-configuration.model";
import {ConfigurationCampaignDTO} from "../../model/club-gigot/action-points/configuration-campaign.model";
import {SaveActionPointsDTO} from "../../model/club-gigot/action-points/save-action-point.model";
import {HTTPMethod} from "../../model/http-method.enum";
import {HTTPRequestBuilder} from "../../model/http.request.model";
import {FileService} from "../../core/services/file-service";
import {EnvironmentService} from "../environment.service";
import {HTTPService} from "../http.service";

/**
	 * @description
	 *  Servicio reflejo del controlador Action Points.
	 */

@Injectable({ providedIn: "root" })
export class ActionPointsService implements Controller {

    private API_ACTIONPOINT_URL = this.environmentService.backendHost.href + "api/Points/uploadEarnedPoints";

    constructor(private fileService: FileService, private httpService: HTTPService, private environmentService: EnvironmentService) {}

    public hostPath(): string {
        return this.environmentService.rewardsBackendHost.href;
    }

    public basePath(): string {
        return "api/actionPoints";
    }

    public baseUserPointsPath(): string {
        return "api/userPoints";
    }

    public copyActionPointConfiguration(creationData: ActionPointCampaignConfiguration): Observable<void>{
        return this.httpService.send<ActionPointCampaignConfiguration, void>(
            new HTTPRequestBuilder<ActionPointCampaignConfiguration>()
                .host(this.hostPath())
                .method(HTTPMethod.POST)
                .controllerPath(this.basePath())
                .payload(creationData)
                .build());
    }

    public getActionPointConfiguration(campaignSequence: number): Observable<ActionPointsDTO>{
        return this.httpService.send<typeof undefined, ActionPointsDTO>(
            new HTTPRequestBuilder<typeof undefined>()
                .host(this.hostPath())
                .method(HTTPMethod.GET)
                .controllerPath(this.basePath())
                .relativePath(`${campaignSequence}`)
                .build());
    }

    public saveActionPointConfiguration(campaignSequence: number, saveActionPointsDTO: SaveActionPointsDTO, _isEditMode: boolean): Observable<void>{
        return this.httpService.send<SaveActionPointsDTO, void>(
            new HTTPRequestBuilder<SaveActionPointsDTO>()
                .method(_isEditMode ? HTTPMethod.PATCH : HTTPMethod.POST)
                .host(this.hostPath())
                .controllerPath(this.basePath())
                .relativePath(`${campaignSequence}`)
                .payload(saveActionPointsDTO)
                .build());
    }

    public deleteOneActionPoint(configId: number): Observable<void>{
        return this.httpService.send<typeof undefined, void>(
            new HTTPRequestBuilder<typeof undefined>()
                .host(this.hostPath())
                .method(HTTPMethod.DELETE)
                .relativePath(`delete/one/${configId}`)
                .controllerPath(this.basePath())
                .build());
    }

    public deleteAllActionPoint(campaignSequence: number): Observable<void>{
        return this.httpService.send<typeof undefined, void>(
            new HTTPRequestBuilder<typeof undefined>()
                .host(this.hostPath())
                .method(HTTPMethod.DELETE)
                .relativePath(`delete/all/${campaignSequence}`)
                .controllerPath(this.basePath())
                .build());
    }


    public getCampaignActionPointsStatus(): Observable<ConfigurationCampaignDTO[]> {
        return this.httpService.send<typeof undefined, ConfigurationCampaignDTO[]>(
            new HTTPRequestBuilder<typeof undefined>()
                .host(this.hostPath())
                .method(HTTPMethod.GET)
                .controllerPath(this.basePath())
                .relativePath('campaign-status')
                .build());
    }

    public readActionPointsFile(element): Observable<any> {
        return this.fileService.fileUpload(element, this.API_ACTIONPOINT_URL);
    }

    public loadActionPoints(element): Observable<any> {
        return this.httpService.send<any, any>(
            new HTTPRequestBuilder<any>()
                .host(this.hostPath())
                .method(HTTPMethod.POST)
                .controllerPath(this.baseUserPointsPath())
                .relativePath('uploadEarnedPoints')
                .payload(element)
                .build()
        );
    }

    public saveRequiredProductGroup(group: AdvanceSaleRequiredProductGroup) {
        return this.httpService.send<any, any>(
            new HTTPRequestBuilder<any>()
                .host(this.hostPath())
                .method(HTTPMethod.POST)
                .controllerPath(this.basePath())
                .relativePath('required-group')
                .payload(group)
                .build()
        );
    }

    public updateRequiredProductGroup(group: AdvanceSaleRequiredProductGroup) {
        return this.httpService.send<any, any>(
            new HTTPRequestBuilder<any>()
                .host(this.hostPath())
                .method(HTTPMethod.PATCH)
                .controllerPath(this.basePath())
                .relativePath(`required-group/${group.id}`)
                .payload(group)
                .build()
        );
    }

    public saveAdvanceSaleLevels(advanceSaleId: number, levels: AdvanceSaleLevel[]) {
        return this.httpService.send<any, any>(
            new HTTPRequestBuilder<any>()
                .host(this.hostPath())
                .method(HTTPMethod.POST)
                .controllerPath(this.basePath())
                .relativePath(`${advanceSaleId}/levels`)
                .payload(levels)
                .build()
        );
    }

    public deleteRequiredGroup(group: AdvanceSaleRequiredProductGroup) {
        return this.httpService.send<any, any>(
            new HTTPRequestBuilder<any>()
                .host(this.hostPath())
                .method(HTTPMethod.DELETE)
                .controllerPath(this.basePath())
                .relativePath(`required-group/${group.id}`)
                .build()
        );
    }

    public createExtraPoints(extraPoints: ExtraPoints, advanceSaleId: number) {
        return this.httpService.send<any, any>(
            new HTTPRequestBuilder<any>()
                .host(this.hostPath())
                .method(HTTPMethod.POST)
                .controllerPath(this.basePath())
                .relativePath(`extraPoints/${advanceSaleId}`)
                .payload(extraPoints)
                .build()
        );
    }

    public updateExtraPoints(extraPoints: ExtraPoints) {
        return this.httpService.send<any, any>(
            new HTTPRequestBuilder<any>()
                .host(this.hostPath())
                .method(HTTPMethod.PATCH)
                .controllerPath(this.basePath())
                .relativePath(`extraPoints/${extraPoints.id}`)
                .payload(extraPoints)
                .build()
        );
    }

    public deleteExtraPoints(extraPointsId: number) {
        return this.httpService.send<any, any>(
            new HTTPRequestBuilder<any>()
                .host(this.hostPath())
                .method(HTTPMethod.DELETE)
                .controllerPath(this.basePath())
                .relativePath(`extraPoints/${extraPointsId}`)
                .build()
        );
    }


    public verifyActionPoints(actionPointsConfigurationId: number): Observable<boolean> {
      return this.httpService.send<number, boolean>(
        new HTTPRequestBuilder<any>()
          .host(this.hostPath())
          .method(HTTPMethod.POST)
          .controllerPath(this.basePath())
          .relativePath(`verify-action-points/${actionPointsConfigurationId}`)
          .build())
    }

}
